import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDatabase } from '../context/DatabaseContext';
import { useToast } from '../context/ToastContext';
import {
    ListTodo,
    Clock,
    CheckCircle,
    AlertCircle,
    Loader,
    Filter,
    Calendar,
    Trash2,
    ExternalLink,
    GitBranch,
} from 'lucide-react';
import { listPlans, deletePlan } from '../services/api';
import { formatLocalDate } from '../utils/timezone';

const getTimeFrameLabels = (t) => ({
    'yesterday': t('plansLanding.timeFrames.yesterday'),
    'week_to_date': t('plansLanding.timeFrames.weekToDate'),
    'last_week': t('plansLanding.timeFrames.lastWeek'),
    'month_to_date': t('plansLanding.timeFrames.monthToDate'),
    'last_month': t('plansLanding.timeFrames.lastMonth'),
    'custom': t('plansLanding.timeFrames.customRange'),
});

const PRIORITY_COLORS = {
    'HIGH': 'bg-red-100 text-red-800',
    'MEDIUM': 'bg-orange-100 text-orange-800',
    'LOW': 'bg-yellow-100 text-yellow-800',
};

export const PlansLanding = () => {
    const { t } = useTranslation(['pages-misc-3', 'common']);
    const navigate = useNavigate();
    const { databases } = useDatabase();
    const { showToast } = useToast();
    const TIME_FRAME_LABELS = getTimeFrameLabels(t);

    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterDatabase, setFilterDatabase] = useState('');
    const [filterTimeFrame, setFilterTimeFrame] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    const fetchPlans = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filterDatabase) params.databaseId = parseInt(filterDatabase);
            if (filterTimeFrame) params.timeFrameType = filterTimeFrame;
            if (filterStatus) params.status = filterStatus;
            
            const response = await listPlans(params);
            setPlans(response.data?.data || []);
        } catch (error) {
            console.error('Failed to fetch plans:', error);
            showToast(t('plansLanding.failedToLoadPlans'), 'error');
        } finally {
            setLoading(false);
        }
    }, [filterDatabase, filterTimeFrame, filterStatus, showToast, t]);

    useEffect(() => {
        fetchPlans();
    }, [fetchPlans]);

    const handleDeletePlan = async (planId, e) => {
        e.stopPropagation();
        if (!window.confirm(t('plansLanding.confirmDeletePlan'))) return;

        try {
            await deletePlan(planId);
            showToast(t('plansLanding.planDeletedSuccessfully'), 'success');
            fetchPlans();
        } catch (error) {
            console.error('Failed to delete plan:', error);
            showToast(t('plansLanding.failedToDeletePlan'), 'error');
        }
    };

    const handleViewPlan = (plan) => {
        const db = plan.database_connection_id;
        if (!db) return;
        const qs = new URLSearchParams({ planId: String(plan.id) });
        if (plan.report_generation_id != null) {
            qs.set('reportId', String(plan.report_generation_id));
        }
        if (plan.time_frame_type) {
            qs.set('timeFrameType', plan.time_frame_type);
        }
        navigate(`/reports/${db}/plan?${qs.toString()}`);
    };

    const handleOpenContinued = (plan, e) => {
        e.stopPropagation();
        if (!plan.continued_by_plan_id || !plan.database_connection_id) return;
        const qs = new URLSearchParams({ planId: String(plan.continued_by_plan_id) });
        if (plan.report_generation_id != null) {
            qs.set('reportId', String(plan.report_generation_id));
        }
        if (plan.time_frame_type) {
            qs.set('timeFrameType', plan.time_frame_type);
        }
        navigate(`/reports/${plan.database_connection_id}/plan?${qs.toString()}`);
    };

    const getStats = () => {
        const active = plans.filter(p => p.status === 'active').length;
        const implemented = plans.filter(p => p.implemented).length;
        const evaluated = plans.filter(p => p.latest_evaluation).length;
        return { active, implemented, evaluated, total: plans.length };
    };

    const stats = getStats();

    return (
        <div className="page-content">
            <div className="mb-8">
                <h1 className="page-title mb-2">{t('plansLanding.title')}</h1>
                <p className="text-gray-600">
                    {t('plansLanding.subtitle')}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 mb-1">{t('plansLanding.stats.totalPlans')}</p>
                            <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                            <ListTodo className="w-6 h-6 text-blue-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 mb-1">{t('common:active')}</p>
                            <p className="text-3xl font-bold text-orange-600">{stats.active}</p>
                        </div>
                        <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
                            <Clock className="w-6 h-6 text-orange-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 mb-1">{t('plansLanding.stats.implemented')}</p>
                            <p className="text-3xl font-bold text-green-600">{stats.implemented}</p>
                        </div>
                        <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 mb-1">{t('plansLanding.stats.evaluated')}</p>
                            <p className="text-3xl font-bold text-purple-600">{stats.evaluated}</p>
                        </div>
                        <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center">
                            <AlertCircle className="w-6 h-6 text-purple-600" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                <div className="flex flex-wrap gap-4">
                    <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{t('common:filters')}:</span>
                    </div>

                    <select
                        value={filterDatabase}
                        onChange={(e) => setFilterDatabase(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                        <option value="">{t('plansLanding.allDatabases')}</option>
                        {databases.map(db => (
                            <option key={db.id} value={db.id}>{db.name}</option>
                        ))}
                    </select>

                    <select
                        value={filterTimeFrame}
                        onChange={(e) => setFilterTimeFrame(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                        <option value="">{t('plansLanding.allTimeFrames')}</option>
                        {Object.entries(TIME_FRAME_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>

                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                        <option value="">{t('plansLanding.allStatus')}</option>
                        <option value="active">{t('common:active')}</option>
                        <option value="completed">{t('plansLanding.completed')}</option>
                        <option value="archived">{t('plansLanding.archived')}</option>
                        <option value="superseded">{t('plansLanding.snapshotSuperseded')}</option>
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-primary-600" />
                    <span className="ml-3 text-gray-600">{t('plansLanding.loadingPlans')}</span>
                </div>
            ) : plans.length === 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                    <ListTodo className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t('plansLanding.noPlansFound')}</h3>
                    <p className="text-gray-600">
                        {filterDatabase || filterTimeFrame || filterStatus
                            ? t('plansLanding.tryAdjustingFilters')
                            : t('plansLanding.plansWillAppearHere')}
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('plansLanding.table.plan')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('plansLanding.table.priority')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('plansLanding.table.skills')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('plansLanding.table.timeFrame')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('common:status')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('plansLanding.table.evaluation')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('common:created')}
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('common:actions')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {plans.map((plan) => (
                                <tr
                                    key={plan.id}
                                    onClick={() => handleViewPlan(plan)}
                                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                                >
                                    <td className="px-6 py-4">
                                        <div className="flex items-center">
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    {plan.topic}
                                                </div>
                                                {plan.owner && (
                                                    <div className="text-xs text-gray-500">
                                                        {plan.owner}
                                                    </div>
                                                )}
                                                {plan.supersedes_plan_id && plan.status !== 'superseded' && (
                                                    <div className="text-xs text-sky-700 mt-1">
                                                        {t('plansLanding.continuedFromPlan', { id: plan.supersedes_plan_id })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {plan.priority && (
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${PRIORITY_COLORS[plan.priority] || 'bg-gray-100 text-gray-800'}`}>
                                                {plan.priority}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 align-top max-w-[14rem]">
                                        {plan.skills?.length > 0 ? (
                                            <div className="flex flex-wrap gap-1">
                                                {plan.skills.map((s) => (
                                                    <span
                                                        key={s}
                                                        title={s}
                                                        className="inline-flex max-w-[11rem] truncate px-2 py-0.5 rounded text-[11px] font-mono bg-gray-100 text-gray-800 border border-gray-200"
                                                    >
                                                        {s}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-xs text-gray-400">—</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center text-sm text-gray-600">
                                            <Calendar className="w-4 h-4 mr-1" />
                                            {TIME_FRAME_LABELS[plan.time_frame_type] || plan.time_frame_type}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        {plan.status === 'superseded' ? (
                                            <div className="flex flex-col gap-1">
                                                <span className="inline-flex items-center w-fit px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900 border border-amber-200">
                                                    <GitBranch className="w-3 h-3 mr-1" />
                                                    {t('plansLanding.snapshot')}
                                                </span>
                                                {plan.continued_by_plan_id && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => handleOpenContinued(plan, e)}
                                                        className="text-left text-xs font-semibold text-primary-600 hover:text-primary-800"
                                                    >
                                                        {t('plansLanding.openCurrent', { id: plan.continued_by_plan_id })}
                                                    </button>
                                                )}
                                            </div>
                                        ) : plan.status === 'archived' ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                                {t('plansLanding.archived')}
                                            </span>
                                        ) : plan.status === 'completed' || plan.implemented ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                <CheckCircle className="w-3 h-3 mr-1" />
                                                {plan.status === 'completed' ? t('plansLanding.completed') : t('plansLanding.implemented')}
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                <Clock className="w-3 h-3 mr-1" />
                                                {t('common:active')}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {plan.latest_evaluation ? (
                                            <div className="flex items-center">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                    plan.latest_evaluation.success_score >= 70
                                                        ? 'bg-green-100 text-green-800'
                                                        : plan.latest_evaluation.success_score >= 40
                                                        ? 'bg-yellow-100 text-yellow-800'
                                                        : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {Math.round(plan.latest_evaluation.success_score)}%
                                                </span>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-gray-400">{t('plansLanding.notEvaluated')}</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {plan.created_at ? formatLocalDate(plan.created_at) : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewPlan(plan);
                                            }}
                                            className="text-primary-600 hover:text-primary-900 mr-3"
                                            title={t('plansLanding.viewPlan')}
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={(e) => handleDeletePlan(plan.id, e)}
                                            className="text-red-600 hover:text-red-900"
                                            title={t('plansLanding.deletePlan')}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
